import { useCallback, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import useFCSubscriptionStore from "@hsl/fc-subscription/store";
import { type User } from "@hsl/fc-subscription/types";

import useFirstFocus from "../../../../../hooks/useFirstFocus";
import useTranslation, { Translate } from "../../../../../hooks/useTranslation";
import {
    englishTitlesToItalian,
    italianTitlesToEnglish,
} from "../../../../../lib/italianTitles";
import {
    UserDetailsService,
    type UserDetailsUpdate,
} from "../../../../../openapi";
import Button from "../../../../Button";
import Briefcase from "../../../../icons/Briefcase";
import EnvelopeOpen from "../../../../icons/EnvelopeOpen";
import Globe from "../../../../icons/Globe";
import UserIcon from "../../../../icons/User";
import Select from "../../../../Select";
import Spinner from "../../../../Spinner";
import { useSubscriptionContext } from "../../../Root";
import { Card } from "../../../SubscriptionFlows/Card";
import {
    DetailFormInput,
    InvalidInput,
} from "../../../SubscriptionFlows/Steps/AddDetails/FormComponents";
import { getProfiles } from "../../../SubscriptionFlows/Steps/AddDetails/services";

interface Props {
    user: User;
    titleNullValue?: string;
}

export const EditYourUserDetails = ({
    user,
    titleNullValue = "Select",
}: Props) => {
    const {
        audienceMeta: { userTitles },
        config: { audienceId, jurisdictionName, locale },
    } = useSubscriptionContext();

    const tx = useTranslation();
    const { inputFieldEvent, buttonClickEvent, clickLinkEvent } =
        useYardActionEvents();
    const handleFocus = useFirstFocus<string>((name) =>
        inputFieldEvent({ name }),
    );
    const details = useForm<UserDetailsUpdate>({
        defaultValues: {
            title:
                locale === "it_IT"
                    ? englishTitlesToItalian(user.title || "")
                    : user.title || "",
            firstName: user.firstName || "",
            lastName: user.lastName || "",
            company: user.company || "",
            jobTitle: user.jobTitle || "",
            profile: user.profile || "",
        },
    });
    const updateUser = useFCSubscriptionStore((state) => state.updateUser);
    const logOut = useFCSubscriptionStore((state) => state.logOut);

    const { data: profileOptions } = useQuery({
        queryKey: ["profiles", audienceId],
        queryFn: async () => getProfiles(audienceId),
        cacheTime: Infinity,
        refetchInterval: Infinity,
        refetchOnWindowFocus: false,
    });

    const latestFC = useFCSubscriptionStore((state) => state.latestFC);
    const [params] = useSearchParams();
    const navigate = useNavigate();

    const save = useMutation({
        mutationFn: async (values: UserDetailsUpdate) => {
            await updateUser({
                ...values,
                title:
                    locale === "it_IT"
                        ? italianTitlesToEnglish(values.title)
                        : values.title,
            });
        },
    });

    const titleOptions = [titleNullValue, ...userTitles].map((t) => ({
        key: t,
        label: t === titleNullValue ? `- ${t} -` : t,
        value: t,
    }));

    const onReset = useCallback(() => {
        details.setValue(
            "title",
            locale === "it_IT"
                ? englishTitlesToItalian(user.title || "")
                : user.title || "",
        );
        details.setValue("firstName", user.firstName || "");
        details.setValue("lastName", user.lastName || "");
        details.setValue("company", user.company || "");
        details.setValue("jobTitle", user.jobTitle || "");
        details.setValue("profile", user.profile || "");
        buttonClickEvent({
            name: "Cancel",
        });
    }, [details, user, locale, buttonClickEvent]);

    useEffect(() => {
        // @ts-expect-error cba to type window for this debug command
        window.deleteUser = async () => {
            await UserDetailsService.deleteUser(user.userId);
            window.location.reload();
        };
        return () => {
            // @ts-expect-error see above
            delete window.deleteUser;
        };
    }, [user]);

    useEffect(() => {
        onReset();
    }, [user, onReset]);

    return (
        <div className="mx-auto flex max-w-[898px] flex-col">
            <Card className="mt-8">
                <p>
                    <Translate>
                        You can update your personal details below
                    </Translate>
                </p>
                <form
                    id="user-details-form"
                    className="mt-4 grid grid-cols-1 gap-8 sm:grid-cols-2"
                    onSubmit={details.handleSubmit((values) =>
                        save.mutate(values),
                    )}
                >
                    <div className="flex flex-col gap-8">
                        <DetailFormInput icon={<UserIcon />}>
                            <Controller
                                name="title"
                                control={details.control}
                                rules={{
                                    required: "Please select title",
                                }}
                                render={({ field }) => (
                                    <Select
                                        value={field.value}
                                        options={titleOptions}
                                        onFocus={() =>
                                            handleFocus("title", "Title")
                                        }
                                        onChange={(value: string) => {
                                            const v =
                                                value === titleNullValue
                                                    ? ""
                                                    : value;
                                            details.setValue("title", v);
                                        }}
                                        selectedClassName="text-base"
                                        optionClassName="text-base"
                                        buttonClassName="px-4 py-2.5 border rounded-sm border-taupe-grey"
                                    />
                                )}
                            />
                            <InvalidInput
                                error={details.formState.errors.title}
                            />
                            <input
                                type="text"
                                placeholder={tx("First name").result}
                                onFocus={() =>
                                    handleFocus("fName", "First name")
                                }
                                {...details.register("firstName", {
                                    required: tx("Please enter first name")
                                        .result,
                                    maxLength: {
                                        value: 40,
                                        message: tx(
                                            "First name cannot be longer than 40 characters",
                                        ).result,
                                    },
                                })}
                            />
                            <InvalidInput
                                error={details.formState.errors.firstName}
                            />
                            <input
                                type="text"
                                placeholder={tx("Last name").result}
                                onFocus={() =>
                                    handleFocus("lName", "Last name")
                                }
                                {...details.register("lastName", {
                                    required: tx("Please enter last name")
                                        .result,
                                    maxLength: {
                                        value: 80,
                                        message: tx(
                                            "Last name cannot be longer than 80 characters",
                                        ).result,
                                    },
                                })}
                            />
                            <InvalidInput
                                error={details.formState.errors.lastName}
                            />
                        </DetailFormInput>
                        <DetailFormInput
                            icon={
                                <EnvelopeOpen className="text-battleship-grey" />
                            }
                        >
                            <input
                                type="text"
                                placeholder={tx("Email").result}
                                onFocus={() => handleFocus("email", "Email")}
                                className="bg-white-smoke !border-white-smoke text-battleship-grey"
                                disabled
                                value={user.email}
                            />
                        </DetailFormInput>
                    </div>
                    <div className="flex flex-col gap-8">
                        <DetailFormInput icon={<Briefcase />}>
                            <input
                                type="text"
                                placeholder={tx("Company").result}
                                onFocus={() =>
                                    handleFocus("company", "Company")
                                }
                                {...details.register("company", {
                                    required: tx("Please enter company").result,
                                    maxLength: {
                                        value: 255,
                                        message: tx(
                                            "Company cannot be longer than 255 characters",
                                        ).result,
                                    },
                                })}
                            />
                            <InvalidInput
                                error={details.formState.errors.company}
                            />
                            <input
                                type="text"
                                placeholder={tx("Job title").result}
                                onFocus={() =>
                                    handleFocus("jTitle", "Job title")
                                }
                                {...details.register("jobTitle", {
                                    required: tx("Please enter job title")
                                        .result,
                                    maxLength: {
                                        value: 128,
                                        message: tx(
                                            "Job title cannot be longer than 128 characters",
                                        ).result,
                                    },
                                })}
                            />
                            <InvalidInput
                                error={details.formState.errors.jobTitle}
                            />
                        </DetailFormInput>
                        {profileOptions && (
                            <DetailFormInput
                                icon={
                                    <Globe className="text-battleship-grey" />
                                }
                            >
                                <div className="flex w-full flex-col gap-4">
                                    <p className="mt-3">{jurisdictionName}</p>
                                    <Controller
                                        name="profile"
                                        control={details.control}
                                        rules={{
                                            required: "Please select profile",
                                        }}
                                        render={({ field }) => (
                                            <Select
                                                value={field.value}
                                                options={profileOptions}
                                                onFocus={() =>
                                                    handleFocus(
                                                        "profile",
                                                        "Profile",
                                                    )
                                                }
                                                onChange={(value: string) => {
                                                    details.setValue(
                                                        "profile",
                                                        value as UserDetailsUpdate["profile"],
                                                    );
                                                }}
                                                selectedClassName="text-base"
                                                optionClassName="text-base"
                                                buttonClassName="px-4 py-2.5 border rounded-sm border-taupe-grey"
                                            />
                                        )}
                                    />
                                    <InvalidInput
                                        error={details.formState.errors.profile}
                                    />
                                </div>
                            </DetailFormInput>
                        )}
                    </div>
                </form>
            </Card>
            <div className="mt-10 flex justify-between gap-4">
                <button
                    className="text-core-blue text-base underline"
                    onClick={() => {
                        logOut();
                        navigate(latestFC + "?" + params.toString());
                        clickLinkEvent({ name: "Log out" });
                    }}
                >
                    <Translate>Log out</Translate>
                </button>
                <div className="flex gap-4">
                    <Button color="white" variant="large" onClick={onReset}>
                        <Translate>Cancel</Translate>
                    </Button>
                    <Button
                        color="primary"
                        variant="large"
                        className="flex-row-reverse"
                        type="submit"
                        form="user-details-form"
                        disabled={save.isLoading}
                        onClick={() =>
                            buttonClickEvent({
                                name: "Save",
                            })
                        }
                        icon={
                            save.isLoading ? (
                                <Spinner className="my-0 ml-4 mr-0 h-4 w-4 animate-spin" />
                            ) : undefined
                        }
                    >
                        <Translate>Save</Translate>
                    </Button>
                </div>
            </div>
        </div>
    );
};
